import React from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { Image } from '@fiverr-private/media';
import { Link, Typography } from '@fiverr-private/typography';
import { GoArrowRightIcon } from '@fiverr-private/go_shared_ui';
import { Announcement, AnnouncementLayoutType, AnnouncementType } from '../../../../types/announcement.types';

const formatDate = (date: string) => {
    const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(date).toLocaleDateString('en-US', options);
};

const AnnouncementMessage: React.FC<Announcement> = ({
    id,
    type,
    title,
    description,
    content,
    hyperlink,
    createdAt,
    imageUrl,
    layoutType = AnnouncementLayoutType.REGULAR,
}) => {
    const isLargeLayout = layoutType === AnnouncementLayoutType.LARGE;

    return (
        <Stack
            padding="6"
            gap="8"
            justifyContent="spaceBetween"
            background="rgba(98, 100, 106, 0.2)"
            borderRadius="md"
            direction={{ default: 'column', sm: isLargeLayout ? 'column' : 'row', lg: 'row' }}
        >
            {imageUrl && (
                <Image
                    height={{
                        default: 'auto',
                        sm: isLargeLayout ? 'auto' : '120px',
                        md: isLargeLayout ? 'auto' : '120px',
                        lg: isLargeLayout ? '365px' : '120px',
                    }}
                    width={{
                        default: '100%',
                        sm: isLargeLayout ? '100%' : '160px',
                        md: isLargeLayout ? '100%' : '160px',
                        lg: isLargeLayout ? '487px' : '160px',
                    }}
                    alt={`Announcement-id-${id}`}
                    src={imageUrl}
                    borderRadius="md"
                    alignSelf="center"
                />
            )}
            <Stack direction="column" width="100%" gap={layoutType === AnnouncementLayoutType.LARGE ? '4' : '2'}>
                <Stack justifyContent="spaceBetween" gap="2">
                    <Typography fontWeight="semibold" color="grey_300" size="b_md" flex="1">
                        {title}
                    </Typography>
                    <Typography color="grey_500" size="b_sm" fontFamily="primary">
                        {formatDate(createdAt.toString())}
                    </Typography>
                </Stack>
                <Stack gap="4" direction="column">
                    {content ? (
                        content.map((contentItem, index) => (
                            <Stack key={index} direction="column" gap="1">
                                <Typography fontWeight="semibold" color="grey_300" size="b_sm">
                                    {contentItem.title}
                                </Typography>
                                <Typography color="grey_700" size="b_sm">
                                    {contentItem.description}
                                </Typography>
                            </Stack>
                        ))
                    ) : (
                        <Typography color="grey_700" size="b_sm">
                            {description}
                        </Typography>
                    )}
                </Stack>
                <Stack justifyContent="spaceBetween" alignItems="flexEnd" marginTop="auto">
                    <Stack gap="2">
                        {type === AnnouncementType.PERSONAL_ASSISTANT_UPDATES && (
                            <div
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '50%',
                                    background:
                                        'radial-gradient(circle at 14.7826px 11.3043px, #FFAE72, #F4B5C5 25%, #85A6FF 50%, #3B80DB 75%, white 100%)',
                                }}
                            ></div>
                        )}
                        {type === AnnouncementType.AI_CREATION_MODELS_UPDATE && (
                            <div
                                style={{
                                    width: '12px',
                                    height: '12px',
                                    borderRadius: '2px',
                                    background: 'linear-gradient(180deg, #C73E6A 0%, #F88610 100%)',
                                }}
                            ></div>
                        )}
                        <Typography color="grey_700" size="b_xxs" fontWeight="bold" fontFamily="primary">
                            {type}
                        </Typography>
                    </Stack>
                    {hyperlink && (
                        <Link
                            href={hyperlink.url}
                            target="_blank"
                            color="grey_300"
                            fontWeight="semibold"
                            size="b_sm"
                            decoration="none"
                        >
                            <Stack gap="1">
                                {hyperlink.text}
                                <Stack padding="1">
                                    <GoArrowRightIcon size={16} color="rgba(239, 239, 240, 1)" />
                                </Stack>
                            </Stack>
                        </Link>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};

export { AnnouncementMessage };
